import BoldText from '@/components/BoldText';
import IconButton from '@/components/IconButton';
import { CarIcon, CartIcon, PinLocationIcon } from '@/components/Icons';
import { timelineIcons } from '@/components/Icons/TimelineIcons';
import Order1 from '@/components/Icons/TimelineIcons/Order1';
import { HighlightContentPreviewId, TimelineIconStages } from '@/constants/enum';
import { ITimelineStage } from '@/constants/timeline-graphic';
import useClickHighlight from '@/hooks/useClickHighlight';
import { HighlightPreviewStateKeys } from '@/redux/slice/highlightPreview.slice';
import settingsSlice, { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { Button, Card, Checkbox, TextField } from '@shopify/polaris';
import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface IProps {
  fieldTitle: ITimelineStage['fieldTitle'];
  fieldTooltip: ITimelineStage['fieldTooltip'];
  fieldStatus: ITimelineStage['fieldStatus'];
  stage: TimelineIconStages;
  handleOpenModal: (icon: TimelineIconStages) => void;
  stageNumber: number;
}

function TimelineGraphicCard({ fieldStatus, fieldTitle, fieldTooltip, stage, handleOpenModal, stageNumber }: IProps) {
  const dispatch = useDispatch();
  const { handleClickHighlight } = useClickHighlight();

  const dataSettings = useSelector(dataSettingsSelector);

  const handleChange = (key: keyof typeof dataSettings) => (value: string | number | boolean) => {
    dispatch(
      settingsSlice.actions.handleDataSettings({
        ...dataSettings,
        [key]: value,
      }),
    );
  };

  const filterIcon = () => {
    if (dataSettings[stage]) {
      if (dataSettings[stage]?.isCustom) {
        return (
          <img
            className="timeline-layout--svg--icon"
            src={dataSettings[stage]?.iconUrl}
            alt="custom icon"
            width={30}
            height={30}
            style={{ objectFit: 'cover', objectPosition: 'center' }}
          />
        );
      }
      let icon = timelineIcons
        .map((item) => item.icons)
        .flat()
        .find((item) => +item.id === dataSettings[stage]?.iconId)?.icon;
      if (icon) {
        icon = React.cloneElement(icon, { fill: dataSettings.icon_color });
      }
      return icon;
    }
    switch (stage) {
      case TimelineIconStages.Stage1: {
        return <CartIcon fill={dataSettings.icon_color} />;
      }
      case TimelineIconStages.Stage2: {
        return <CarIcon fill={dataSettings.icon_color} />;
      }
      case TimelineIconStages.Stage3: {
        return <PinLocationIcon fill={dataSettings.icon_color} />;
      }
      default:
        break;
    }
  };

  const filterId = useCallback(
    (type: 'title' | 'tooltip') => {
      switch (stage) {
        case TimelineIconStages.Stage1: {
          return type === 'title'
            ? HighlightContentPreviewId.TimelineOrderStepTitle
            : HighlightContentPreviewId.TimelineOrderStepDescription;
        }
        case TimelineIconStages.Stage2: {
          return type === 'title'
            ? HighlightContentPreviewId.TimelineShipStepTitle
            : HighlightContentPreviewId.TimelineShipStepDescription;
        }
        case TimelineIconStages.Stage3: {
          return type === 'title'
            ? HighlightContentPreviewId.TimelineDeliveryStepTitle
            : HighlightContentPreviewId.TimelineDeliveryStepDescription;
        }
        default: {
          return '';
        }
      }
    },
    [stage],
  );

  const handleHighLight = (type: 'Title' | 'Tooltip' | 'Icon') => {
    setTimeout(() => {
      let key: HighlightPreviewStateKeys | '' = '';
      switch (stage) {
        case TimelineIconStages.Stage1: {
          key = `isHighlightedOrder${type}`;
          break;
        }
        case TimelineIconStages.Stage2: {
          key = `isHighlightedShip${type}`;
          break;
        }
        case TimelineIconStages.Stage3: {
          key = `isHighlightedDelivery${type}`;
          break;
        }
        default: {
          break;
        }
      }
      if (!key) return;
      handleClickHighlight(key)();
    }, 0);
  };

  return (
    <div className={dataSettings[fieldStatus] ? '' : 'inactive-stage'}>
      <Card>
        <div className="d-flex content-between">
          <BoldText>Stage {stageNumber}</BoldText>
          <Checkbox label="Check" labelHidden checked={dataSettings[fieldStatus]} onChange={handleChange(fieldStatus)} />
        </div>
        <div className="mt-8">
          <BoldText>Icon</BoldText>
          <div className="d-flex mt-4">
            <div className="mr-8" onClick={() => handleHighLight('Icon')}>
              <IconButton>{filterIcon() ? filterIcon() : <Order1 />}</IconButton>
            </div>
            <Button variant="plain" onClick={() => handleOpenModal(stage)}>
              Change
            </Button>
          </div>
        </div>
        <div className="mt-4" onClick={() => handleHighLight('Title')}>
          <TextField
            label={'Title'}
            autoComplete="off"
            value={dataSettings[fieldTitle]}
            onChange={handleChange(fieldTitle)}
            id={filterId('title')}
          />
        </div>
        <div className="mt-8" onClick={() => handleHighLight('Tooltip')}>
          <TextField
            label={'Tooltip'}
            autoComplete="off"
            value={dataSettings[fieldTooltip]}
            onChange={handleChange(fieldTooltip)}
            id={filterId('tooltip')}
          />
        </div>
      </Card>
    </div>
  );
}

export default memo(TimelineGraphicCard);
